import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CurrencyFormatter from '../../../Utils/CurrencyFormatter';
import Message from '../../../ComponentMessage';
import ContinueButton from '../components/ContinueButton';
import SignupFormAction from '../components/SignupFormAction';
import DocumentLine from './components/DocumentLine';
import { AdditionalFields, Steps } from '../SignupForm.constants';
import T from '../Locale';
const initialDocumentInfo = {
    value: null,
    canDelete: false,
    error: null
};
const realEstateId = 'real-estate';
const AddDocumentsStep = ({ onSave, updateAdditionalData, partnerId, setBookingPackageInfo, isRealEstateSelected, currentStep, setCurrentStep }) => {
    var _a, _b, _c, _d;
    const [error, setError] = useState(null);
    const [packageInfo, setPackageInfo] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const [createdDocuments, setCreatedDocuments] = useState([]);
    useEffect(() => {
        if (isRealEstateSelected) {
            setCreatedDocuments([{
                    id: realEstateId,
                    value: 'buyer_advice',
                    purchase_type: 'digital_document_advice',
                    error: false,
                    canDelete: false
                }]);
            setLoading(false);
            return;
        }
        axios.get(`/api/package/${partnerId}`).then(({ data }) => {
            setPackageInfo(data);
            setBookingPackageInfo(data);
            let documents = [];
            if (data.is_package) {
                for (let i = 0; i < data.number_of_products; i += 1) {
                    documents = [...documents, Object.assign(Object.assign({}, initialDocumentInfo), { id: `document-${i}`, purchase_type: data.purchase_types[0] })];
                }
            }
            else {
                documents = [...documents, Object.assign(Object.assign({}, initialDocumentInfo), { id: 'document-0', purchase_type: data.purchase_types[0] })];
            }
            setCreatedDocuments(documents);
        }).catch(() => setError(T('partner.documents.genericError')));
    }, [isRealEstateSelected]);
    useEffect(() => {
        if (isLoading && packageInfo != null) {
            setLoading(false);
        }
    }, [packageInfo, isLoading]);
    if (isLoading) {
        return null;
    }
    const availableProducts = isRealEstateSelected ? ['buyer_advice'] : Array.isArray((_a = packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.products) !== null && _a !== void 0 ? _a : []) ? packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.products : Object.values((_b = packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.products) !== null && _b !== void 0 ? _b : {});
    const availablePurchaseTypes = isRealEstateSelected ? ['digital_document_advice'] : Array.isArray((_c = packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.purchase_types) !== null && _c !== void 0 ? _c : []) ? packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.purchase_types : Object.values((_d = packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.purchase_types) !== null && _d !== void 0 ? _d : {});
    return (_jsxs(_Fragment, { children: [error != null && _jsx(Message, { isSuccessful: false, message: error, onClose: () => setError(null) }), _jsx("div", Object.assign({ className: 'w-full' }, { children: (packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.documents_description) != null && _jsx("p", Object.assign({ className: 'text-center font-light' }, { children: packageInfo.documents_description })) })), _jsx(SignupFormAction, Object.assign({ title: T('partner.documents.text') }, { children: createdDocuments.length > 0 && (_jsxs(_Fragment, { children: [createdDocuments.map((document, key) => (_jsx(DocumentLine, { showTitle: key === 0, name: `select-${document.id}`, documentInfo: document, onDelete: document.canDelete ? handleClickDelete : null, onChange: handleChange, availableProducts: availableProducts, availablePurchaseTypes: availablePurchaseTypes, disabled: document.id === realEstateId }, document.id))), (!isRealEstateSelected && !(packageInfo === null || packageInfo === void 0 ? void 0 : packageInfo.is_package)) && (_jsxs("div", Object.assign({ className: 'w-full' }, { children: [_jsxs("button", Object.assign({ "data-behat": 'add-new-product', className: 'text-dark-teal tex-lg focus:outline-none cursor-pointer', type: 'button', onClick: handleClickAddDocument }, { children: [_jsx("i", { className: 'fal fa-plus mr-3' }), T('partner.documents.addProduct')] })), packageInfo.amount != null && (_jsx("div", Object.assign({ className: 'flex w-full justify-center md:justify-end mt-4' }, { children: _jsxs("p", Object.assign({ className: 'text-dark-teal text-lg' }, { children: [T('partner.documents.price'), ": ", _jsx("span", Object.assign({ className: 'font-bold' }, { children: CurrencyFormatter(packageInfo.amount) }))] })) })))] })))] })) })), (onSave != null && !isLoading) && _jsx(ContinueButton, { onClick: handleClickSave, id: 'add-documents' })] }));
    function handleClickAddDocument() {
        const newDocument = Object.assign(Object.assign({}, initialDocumentInfo), { id: `document-${createdDocuments.length}`, canDelete: true, purchase_type: packageInfo.purchase_types[0] });
        setCreatedDocuments((prevState) => ([...prevState, newDocument]));
    }
    function handleClickDelete(id) {
        const filteredDocuments = createdDocuments.filter(document => document.id !== id);
        setCreatedDocuments(filteredDocuments);
    }
    function handleChange(value, name, id) {
        const updatedDocuments = createdDocuments.map((document) => {
            let updatedDocument = Object.assign({}, document);
            if (document.id === id || name === 'purchase_type') {
                updatedDocument = Object.assign(Object.assign({}, updatedDocument), { [name]: value });
            }
            return updatedDocument;
        });
        setCreatedDocuments(updatedDocuments);
        if (currentStep !== Steps.ADD_DOCUMENTS) {
            setCurrentStep(Steps.ADD_DOCUMENTS);
        }
    }
    function handleClickSave() {
        setCreatedDocuments(prevState => prevState.map(document => (Object.assign(Object.assign({}, document), { error: false }))));
        if (createdDocuments.some(document => document.value == null)) {
            setCreatedDocuments(prevState => prevState.map((document) => {
                if (document.value == null) {
                    return Object.assign(Object.assign({}, document), { error: true });
                }
                return document;
            }));
            return;
        }
        else {
            if (new Set(createdDocuments.map(document => document.value)).size !== createdDocuments.length) {
                setError(T('partner.documents.duplicateDocumentsError'));
                return;
            }
        }
        updateDocumentsProperty(createdDocuments);
        onSave();
    }
    function updateDocumentsProperty(documents) {
        const filteredCreatedDocuments = documents.filter(document => document.value != null).map(document => ({ id: document.value, purchase_type: document.purchase_type }));
        updateAdditionalData({ [AdditionalFields.PRODUCTS]: filteredCreatedDocuments }, true);
    }
};
AddDocumentsStep.propTypes = {
    onSave: PropTypes.func,
    updateAdditionalData: PropTypes.func.isRequired,
    partnerId: PropTypes.string,
    setBookingPackageInfo: PropTypes.func,
    isRealEstateSelected: PropTypes.bool
};
export default AddDocumentsStep;
